import { useCallback, useEffect, useMemo, useState } from "react";
import SpinningLoader from "@card-capture/app/components/SpinningLoader";
import {
  CardCaptureIframe,
  CardCaptureIframeProps,
  getCardCaptureIframeHeight,
} from "@card-capture/app/components/CardCaptureIframe";
import type { TokeniseResponse } from "@card-capture-lib/app/model/config.model";
import BinIcon from "./BinIcon";
import { Button } from "./Button";
import { ConfirmDialog } from "./ConfirmDialog";
import type { StoredPaymentMethod } from "@checkout-customer-api/model/storedPaymentMethod.model";
import {
  isPaymentInstrument,
  isAuthenticated,
} from "@checkout-customer-api/model/storedPaymentMethod.model";
import { isPaid } from "@checkout-customer-api/model/paymentIntent.model";
import type { PaymentIntentError } from "@checkout-customer-api/model/paymentIntent.model";
import { useAppContext, errorTypeFrom } from "../context/AppContext";
import { assembleCardCaptureOptions } from "../model/cardCaptureOptions.model";
import { formatCurrencyAmount } from "../utils/currency.utils";
import css from "./PaymentMethodList.module.css";

type State = { status: "loading" } | { status: "loaded"; cards: StoredPaymentMethod[] };

type DeleteState =
  | { status: "idle" }
  | { status: "confirming"; card: StoredPaymentMethod }
  | { status: "deleting"; card: StoredPaymentMethod };

type SavedCardPaymentState = { status: "idle" } | { status: "confirming" };

type CancelState = "idle" | "confirming";

const NEW_CARD_ID = "new-card";

export type ErrorMessageValue = string | PaymentIntentError | null;

export function PaymentMethodList({
  onErrorChange: setErrorMessage,
}: Readonly<{ onErrorChange: (error: ErrorMessageValue) => void }>) {
  const {
    client,
    environment,
    authToken,
    options,
    orderDetails,
    authenticationId: initialAuthenticationId,
    paymentIntentId,
    isMitRegistration,
    redirect,
  } = useAppContext();
  const formattedAmount = formatCurrencyAmount(orderDetails.amount, orderDetails.currency);
  const [state, setState] = useState<State>({ status: "loading" });
  const [selectedId, setSelectedId] = useState<string>(NEW_CARD_ID);
  const [cardCaptureAuthenticationId, setCardCaptureAuthenticationId] =
    useState(initialAuthenticationId);
  const [isCardCaptureProcessing, setIsCardCaptureProcessing] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState>({ status: "idle" });
  const [savedCardPaymentState, setSavedCardPaymentState] = useState<SavedCardPaymentState>({
    status: "idle",
  });
  const [cancelState, setCancelState] = useState<CancelState>("idle");

  const setErrorMessageIfPresent = useCallback(
    (error?: PaymentIntentError) => {
      if (error) {
        setErrorMessage(error);
      }
    },
    [setErrorMessage],
  );

  const cardCaptureOptions = useMemo(
    () =>
      assembleCardCaptureOptions(
        options,
        cardCaptureAuthenticationId,
        formattedAmount,
        isMitRegistration,
      ),
    [options, cardCaptureAuthenticationId, formattedAmount, isMitRegistration],
  );

  const payButtonText = cardCaptureOptions.theme?.text?.submitButton ?? `Pay ${formattedAmount}`;

  const onTokenize = useCallback(
    (response: TokeniseResponse) => {
      // An MIT registration always saves the card (CIT/INITIAL); the merchant's cof_type on the
      // intent makes the stored payment method MIT-capable.
      const saveCard = isMitRegistration || response.saveCard === true;
      client
        .confirmWithCard(paymentIntentId, response.token, saveCard)
        .then((intent) => {
          if (isPaid(intent)) {
            redirect({ status: "SUCCESS", paymentIntentId: intent.id });
            return;
          }

          if (intent.status === "REQUIRES_PAYMENT_METHOD") {
            setCardCaptureAuthenticationId(intent.authentication_id);
            setIsCardCaptureProcessing(false);
            setErrorMessageIfPresent(intent.error);
            return;
          }

          // TODO that's the scope of CNP-4264
          if (intent.status === "REQUIRES_AUTHENTICATION") {
            redirect({
              status: "ERROR",
              error: "REQUIRES_AUTHENTICATION",
              paymentIntentId: intent.id,
            });
            return;
          }

          redirect({
            status: "ERROR",
            error: intent.error?.type ?? "CLIENT_INTERNAL_ERROR",
            code: intent.error?.code,
            paymentIntentId: intent.id,
          });
        })
        .catch((error: unknown) => {
          redirect({ status: "ERROR", error: errorTypeFrom(error), paymentIntentId });
        });
    },
    [client, paymentIntentId, isMitRegistration, redirect, setErrorMessageIfPresent],
  );

  const resetCardCapture = useCallback(() => {
    setIsCardCaptureProcessing(true);
    client
      .pollNewAuthentication(paymentIntentId)
      .then((intent) => {
        setCardCaptureAuthenticationId(intent.authentication_id);
        setErrorMessageIfPresent(intent.error);
      })
      .catch((error: unknown) => {
        redirect({ status: "ERROR", error: errorTypeFrom(error), paymentIntentId });
      })
      .finally(() => setIsCardCaptureProcessing(false));
  }, [client, paymentIntentId, redirect, setErrorMessageIfPresent]);

  const cardCaptureCallbacks = useMemo(
    () => ({
      onSubmit: () => {
        setErrorMessage(null);
        setIsCardCaptureProcessing(true);
      },
      onTokenize,
      // Cancelled challenge, client error, or verification failure: poll for a fresh
      // authentication_id and remount. A message is only shown if the polled intent itself
      // carries an error.
      onCancel: resetCardCapture,
      onError: resetCardCapture,
      onVerificationFailure: resetCardCapture,
    }),
    [onTokenize, resetCardCapture, setErrorMessage],
  );

  // TODO: pass an AbortSignal (aborted on cleanup) once getStoredPaymentMethods supports one, to
  // cancel the in-flight fetch instead of letting a stale response still land.
  const refetch = useCallback(async () => {
    if (isMitRegistration) {
      // Registering a card for future MITs never offers saved cards: show the no-cards layout.
      setState({ status: "loaded", cards: [] });
      return;
    }
    setState({ status: "loading" });
    try {
      const spms = await client.getStoredPaymentMethods();
      const eligible = spms.filter((spm) => !isPaymentInstrument(spm) && isAuthenticated(spm));
      setState({ status: "loaded", cards: eligible });
      setSelectedId(eligible.length > 0 ? eligible[0].id : NEW_CARD_ID);
    } catch {
      setState({ status: "loaded", cards: [] });
      setSelectedId(NEW_CARD_ID);
    }
  }, [client, isMitRegistration]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const handleDeleteClick = (card: StoredPaymentMethod) => {
    setSelectedId(card.id);
    setErrorMessage(null);
    setDeleteState({ status: "confirming", card });
  };

  const handleCancelDelete = () => {
    setDeleteState({ status: "idle" });
  };

  const handleConfirmDelete = async () => {
    if (deleteState.status === "idle") {
      return;
    }
    const { card } = deleteState;
    setDeleteState({ status: "deleting", card });
    try {
      await client.deleteStoredPaymentMethod(card.id);
      setDeleteState({ status: "idle" });
      await refetch();
    } catch {
      setDeleteState({ status: "idle" });
      setErrorMessage(`We couldn't delete the card ending in ${card.card_mask}. Please try again.`);
    }
  };

  const handlePayClick = async () => {
    setErrorMessage(null);
    setSavedCardPaymentState({ status: "confirming" });
    try {
      const intent = await client.confirmWithSavedCard(paymentIntentId, selectedId);
      if (isPaid(intent)) {
        redirect({ status: "SUCCESS", paymentIntentId: intent.id });
        return;
      }

      if (intent.status === "REQUIRES_PAYMENT_METHOD") {
        setSavedCardPaymentState({ status: "idle" });
        setErrorMessageIfPresent(intent.error);
        // The card list itself hasn't changed — just re-apply CNP-4250's default-selection rule
        // (first saved card pre-selected), no need to refetch it.
        setSelectedId(
          state.status === "loaded" && state.cards.length > 0 ? state.cards[0].id : NEW_CARD_ID,
        );
        return;
      }

      // TODO that's the scope of CNP-4264
      if (intent.status === "REQUIRES_AUTHENTICATION") {
        redirect({
          status: "ERROR",
          error: "REQUIRES_AUTHENTICATION",
          paymentIntentId: intent.id,
        });
        return;
      }

      redirect({
        status: "ERROR",
        error: intent.error?.type ?? "CLIENT_INTERNAL_ERROR",
        code: intent.error?.code,
        paymentIntentId: intent.id,
      });
    } catch (error) {
      setSavedCardPaymentState({ status: "idle" });
      redirect({ status: "ERROR", error: errorTypeFrom(error), paymentIntentId });
    }
  };

  const isBusy =
    state.status === "loading" ||
    deleteState.status === "deleting" ||
    savedCardPaymentState.status === "confirming";

  // TODO: swaps the root element type, remounting CardCaptureContainer below on every
  // loading/deleting transition. Fixed on CNP-4358-card-capture-remount-fix.
  if (isBusy) {
    return <SpinningLoader />;
  }

  const { cards } = state;

  const cardCapture = (
    <CardCaptureContainer
      key={cardCaptureAuthenticationId}
      isSelected={selectedId === NEW_CARD_ID}
      environment={environment}
      accessToken={authToken}
      options={cardCaptureOptions}
      callbacks={cardCaptureCallbacks}
    />
  );

  const cancelControl = (
    <>
      <Button
        variant="text"
        disabled={isCardCaptureProcessing || deleteState.status !== "idle"}
        onClick={() => setCancelState("confirming")}
      >
        Cancel
      </Button>
      {cancelState === "confirming" && (
        <ConfirmDialog
          message="Do you want to cancel the payment"
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirm={() => redirect({ status: "CANCEL", paymentIntentId })}
          onCancel={() => setCancelState("idle")}
        />
      )}
    </>
  );

  // No saved cards: expand the iframe directly, no list, no "Pay with a new card" row.
  // TODO: this root-swap remounts CardCaptureContainer whenever cards.length crosses zero.
  // Fixed on CNP-4358-card-capture-remount-fix.
  if (cards.length === 0) {
    return (
      <>
        {cardCapture}
        {cancelControl}
      </>
    );
  }

  return (
    <div className="col gap-s">
      <fieldset disabled={isCardCaptureProcessing}>
        <legend>Pay with card</legend>
        <div className="col gap-s">
          {cards.map((card) => (
            <SavedCardContainer
              key={card.id}
              card={card}
              isSelected={selectedId === card.id}
              onSelect={(card: StoredPaymentMethod) => setSelectedId(card.id)}
              onDelete={() => handleDeleteClick(card)}
            />
          ))}

          <label
            className={`${css["payment-method"]} ${selectedId === NEW_CARD_ID ? css.selected : ""}`}
          >
            <input
              type="radio"
              name="payment-method"
              value={NEW_CARD_ID}
              checked={selectedId === NEW_CARD_ID}
              onChange={() => setSelectedId(NEW_CARD_ID)}
            />
            <img src="/img/card.svg" alt="Add new card" className={css.logo} />
            <div className={css.details}>
              <div className={css.line1}>Pay with a new card</div>
              <div className={css.line2}>Enter your card details</div>
            </div>
          </label>
        </div>
      </fieldset>

      {cardCapture}

      {selectedId !== NEW_CARD_ID && (
        <button type="button" onClick={() => void handlePayClick()}>
          {payButtonText}
        </button>
      )}

      {cancelControl}

      {deleteState.status === "confirming" && (
        <ConfirmDialog
          message={`Are you sure you want to delete the card ending in ${deleteState.card.card_mask}?`}
          confirmLabel="Delete"
          onConfirm={() => void handleConfirmDelete()}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
}

function CardCaptureContainer({
  isSelected,
  callbacks,
  ...cardCaptureProps
}: Readonly<{ isSelected: boolean }> & CardCaptureIframeProps) {
  const [iframeReady, setIframeReady] = useState(false);

  // Stable reference across the setIframeReady(true) re-render, so CardCaptureIframe's own effect
  // doesn't see a changed `callbacks` prop and re-register its postMessage channels.
  const callbacksWithReadyState = useMemo(
    () => ({ ...callbacks, onPageLoaded: () => setIframeReady(true) }),
    [callbacks],
  );

  return (
    <div
      className={css["card-capture-container"]}
      style={{
        display: isSelected ? undefined : "none",
        minHeight: getCardCaptureIframeHeight(cardCaptureProps.options),
      }}
    >
      {!iframeReady && <SpinningLoader variant="container" />}
      <div style={{ display: iframeReady ? undefined : "none" }}>
        <CardCaptureIframe {...cardCaptureProps} callbacks={callbacksWithReadyState} />
      </div>
    </div>
  );
}

function SavedCardContainer({
  card,
  isSelected,
  onSelect,
  onDelete,
}: Readonly<{
  card: StoredPaymentMethod;
  isSelected: boolean;
  onSelect: (card: StoredPaymentMethod) => void;
  onDelete: () => void;
}>) {
  return (
    <label key={card.id} className={`${css["payment-method"]} ${isSelected ? css.selected : ""}`}>
      <input
        type="radio"
        name="payment-method"
        value={card.id}
        checked={isSelected}
        onChange={() => onSelect(card)}
      />
      <img
        src={`/img/cards/${card.card_type.toLowerCase()}.png`}
        alt={card.card_type}
        className={css.logo}
      />
      <div className={css.details}>
        <span className={css.line1}>•••• {card.card_mask}</span>
        <span className={css.line2}>Expires {card.card_expiry}</span>
      </div>
      <button
        type="button"
        className={css["delete-button"]}
        aria-label="Delete card"
        onClick={onDelete}
      >
        <BinIcon />
      </button>
    </label>
  );
}
